import { useMutation } from '@apollo/client';
import { faUpload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldHookConfig, useField } from 'formik';
import React, { useEffect, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { ImageReference } from '../../types/MediaReference';
import { FileReference, UploadStatus, makeFileKey } from './FileUpload';
import UploadProfileMedia from '../../graphql/UploadProfileMedia.graphql';

interface BannerPictureUploadProps {

}

export function BannerPictureUpload(props: BannerPictureUploadProps & FieldHookConfig<FileReference | null>) {
    const [file, setFile] = useState<FileReference | null>(null);
    
    const [ field, meta, helpers ] = useField(props);
    const [ uploadFile ] = useMutation<{ uploadProfileMedia: ImageReference }, { file: File, mediaType: 'BANNER' | 'PROFILE_PICTURE' }>(UploadProfileMedia);

    useEffect(() => {
        const fileUpload = async (target: FileReference | null) => {
            if (target) {
                setFile({
                    ...target,
                    uploadStatus: UploadStatus.InProgress
                });
    
                // Upload the file via the GraphQL mutation endpoint
                const uploadResponse = await uploadFile({
                    variables: {
                        file: target.localFile,
                        mediaType: 'BANNER'
                    }
                });
    
                // Update the file reference once the upload has completed
                setFile({
                    ...target,
                    hostFilename: uploadResponse.data?.uploadProfileMedia.url,
                    uploadStatus: UploadStatus.Completed
                });
            }   
        }

        // Executes whenever the file reference state is changed
        // Verify the file is awaiting upload
        if (file?.uploadStatus === UploadStatus.NotStarted) {
            // Initiate the upload
            fileUpload(file);
        }

        if (file) {
            helpers.setValue(file);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file, uploadFile]);

    const { isDragActive, getRootProps, getInputProps, open } = useDropzone({
        noClick: true,
        maxFiles: 1,
        // File size limited to 5 MB
        maxSize: 5 * 1024 * 1024,
        accept: 'image/*',
        onDropAccepted: newFiles => onDropAcceptedHandler(newFiles),
        onDropRejected: rejected => onDropRejectedHandler(rejected)
    });

    const onDropAcceptedHandler = (newFiles: File[]) => {
        // Select the first file from the array (the number of files should be limited to 1)
        const uploadedFile = newFiles[0];
        // Build the file reference object
        const fileReference: FileReference = {
            key: makeFileKey(uploadFile.name),
            localFile: uploadedFile,
            previewUrl: URL.createObjectURL(uploadedFile),
            uploadStatus: UploadStatus.NotStarted
        };
        // Set the file state with the above reference
        setFile(fileReference);
    }

    const onDropRejectedHandler = (rejected: FileRejection[]) => {
        helpers.setError(rejected[0].errors.map(e => e.message).join('; '));
    }

    return (
        <div>
            <div className={`flex items-center justify-center w-32 h-32 rounded-full border-2 border-dashed ${isDragActive ? 'border-loving-500' : 'border-gray-300'}`} {...getRootProps()}>
                <label
                    htmlFor={`media_upload_${field.name}`}
                    className='flex items-center text-gray-700 text-sm font-medium'>
                    <FontAwesomeIcon icon={faUpload} className='text-base text-loving-500 mr-2' />
                    <span>Upload</span>
                    <input
                        type='file'
                        id={`media_upload_${field.name}`}
                        name={`media_upload_${field.name}`}
                        className='sr-only'
                        {...getInputProps()} />
                </label>
            </div>
        </div>
    )
}
